/**
 * 計算 .text 元素之前的 <img> 數量
 * 注意：br 一定是跟著 IMG 的，所以只需要計算 IMG
 */
export const countPrecedingLineElements = (textElement) => {
  let count = 0;
  let node = textElement.previousSibling;
  
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
      count++;
    }
    node = node.previousSibling;
  }
  
  return count;
};

/**
 * 遞迴收集 .text 元素內的所有文字和算式節點
 * @returns {Array<{type: 'text'|'equation'|'html', content: string}>}
 */
export const collectTextNodes = (textElement) => {
  const nodes = [];
  
  const collect = (node, result) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.trim()) {
        // 將文字拆成字元，逐個處理以便精確切分
        for (const char of text) {
          result.push({ type: 'text', content: char });
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const isEquation = node.classList && node.classList.contains('MTConvertedEquation');
      
      if (isEquation) {
        // 算式：作為不可分割單元
        result.push({ type: 'equation', content: node.outerHTML });
      } else if (node.tagName === 'BR') {
        // <br> 標籤：作為不可分割單元，保留標籤
        result.push({ type: 'html', content: '<br>' });
      } else {
        // 其他元素（如 <span>、<div>）：保留整個元素及其樣式
        // 但需要遞迴處理子節點，以便能夠在元素內部進行分頁
        
        // 遞迴檢查元素是否只包含簡單內容（文字、<br>，或只包含簡單內容的其他元素）
        const isSimpleElement = (element) => {
          if (!element.childNodes || element.childNodes.length === 0) {
            return true; // 空元素視為簡單元素
          }
          
          return Array.from(element.childNodes).every(child => {
            if (child.nodeType === Node.TEXT_NODE) {
              return true;
            }
            if (child.nodeType === Node.ELEMENT_NODE) {
              if (child.tagName === 'BR') {
                return true;
              }
              // 遞迴檢查子元素是否也是簡單元素
              return isSimpleElement(child);
            }
            return false;
          });
        };
        
        if (isSimpleElement(node)) {
          // 檢查元素是否只包含 <br>（沒有文字）
          const hasOnlyBr = (element) => {
            if (!element.childNodes || element.childNodes.length === 0) {
              return false;
            }
            return Array.from(element.childNodes).every(child => 
              child.nodeType === Node.ELEMENT_NODE && child.tagName === 'BR'
            );
          };
          
          // 檢查元素是否有文字內容
          const hasTextContent = (element) => {
            if (!element.childNodes || element.childNodes.length === 0) {
              return false;
            }
            return Array.from(element.childNodes).some(child => {
              if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
                return true;
              }
              if (child.nodeType === Node.ELEMENT_NODE) {
                return hasTextContent(child);
              }
              return false;
            });
          };
          
          // 如果元素只包含 <br> 且沒有文字，不要保留標籤，直接輸出 <br>
          if (hasOnlyBr(node) && !hasTextContent(node)) {
            // 只遞迴處理子節點（br），不保留外層標籤
            Array.from(node.childNodes).forEach(child => {
              collect(child, result);
            });
          } else {
            // 如果只包含簡單內容且有文字，保留元素標籤，但內容需要遞迴處理
            // 使用 cloneNode 來獲取完整的開始標籤，完全保留原始格式（包括引號樣式）
            const clonedNode = node.cloneNode(false); // 只複製節點本身，不包含子節點
            const fullTag = clonedNode.outerHTML; // 這會得到 <span style=' color:#0070C0'></span> 或 <div></div>
            
            // 從完整標籤中提取開始標籤（移除結束標籤部分）
            // 例如：<span style=' color:#0070C0'></span> -> <span style=' color:#0070C0'>
            const tagName = node.tagName.toLowerCase();
            const openTag = fullTag.replace(new RegExp(`</${tagName}>$`), ''); // 移除結尾的 </tagName>
            const closeTag = `</${tagName}>`;
            
            // 先加入開始標籤
            result.push({ type: 'html', content: openTag });
            
            // 遞迴處理子節點
            Array.from(node.childNodes).forEach(child => {
              collect(child, result);
            });
            
            // 加入結束標籤
            result.push({ type: 'html', content: closeTag });
          }
        } else {
          // 複雜結構，遞迴處理子節點（不保留外層標籤）
          Array.from(node.childNodes).forEach(child => {
            collect(child, result);
          });
        }
      }
    }
  };
  
  Array.from(textElement.childNodes).forEach(node => {
    collect(node, nodes);
  });
  
  return nodes;
};

/**
 * 將節點陣列切分成多頁
 */
export const splitNodesIntoPages = (nodes, precedingImgCount, pageSize, measureContainer, lineHeight, titleHeight , titleLines ) => {
  const pages = [];
  let currentPageHTML = '';
  
  const maxHeight = pageSize * lineHeight;
  const firstPageMaxHeight = titleHeight > 0 ? maxHeight - titleHeight : maxHeight;
  
  // console.log('=== splitNodesIntoPages: 分頁計算開始 ===');
  // console.log('pageSize (每頁行數)', pageSize);
  // console.log('lineHeight (行高像素)', lineHeight);
  // console.log('maxHeight (每頁最大高度像素)', maxHeight);
  // console.log('precedingImgCount (前置圖片數量)', precedingImgCount);
  // console.log('titleHeight (title高度像素)', titleHeight);
  // console.log('titleLines (title行數)', titleLines);
  // console.log('firstPageMaxHeight (第一頁最大高度像素)', firstPageMaxHeight);
  
  nodes.forEach((node, nodeIndex) => {
    const nodeContent = node.content;
    
    if (!nodeContent) return;
    
    const isFirstPage = pages.length === 0;
    
    // 第一頁需要扣除前置圖片和 .title 的高度
    const currentPageMaxHeight = isFirstPage ? firstPageMaxHeight : maxHeight;
    const availableLines = isFirstPage ? pageSize - precedingImgCount - titleLines : pageSize;
    const maxHeightForLines = availableLines * lineHeight;
    
    // 使用較小的值作為限制（高度限制和行數限制取較嚴格者）
    const effectiveMaxHeight = Math.min(currentPageMaxHeight, maxHeightForLines);
    
    // if (isFirstPage && nodeIndex === 0) {
    //   console.log('--- 第一頁第一個節點 ---');
    //   console.log('availableLines (可用行數)', availableLines);
    //   console.log('計算公式: pageSize - precedingImgCount - titleLines = ' + pageSize + ' - ' + precedingImgCount + ' - ' + titleLines + ' = ' + availableLines);
    //   console.log('maxHeightForLines (行數限制對應的高度像素)', maxHeightForLines);
    //   console.log('計算公式: availableLines * lineHeight = ' + availableLines + ' * ' + lineHeight + ' = ' + maxHeightForLines);
    //   console.log('effectiveMaxHeight (有效最大高度像素)', effectiveMaxHeight);
    //   console.log('計算公式: Math.min(currentPageMaxHeight, maxHeightForLines) = Math.min(' + currentPageMaxHeight + ', ' + maxHeightForLines + ') = ' + effectiveMaxHeight);
    // }
    
    // 測量加入當前節點後的總行數
    const testHTML = `<div class="text">${currentPageHTML + nodeContent}</div>`;
    // 使用 setHTMLAndApplyBrStyles 方法，自動處理 <br> 標籤的樣式
    if (measureContainer.setHTMLAndApplyBrStyles) {
      measureContainer.setHTMLAndApplyBrStyles(testHTML);
    } else {
      measureContainer.innerHTML = testHTML;
    }
    
    const testHeight = measureContainer.offsetHeight;
    
    // 直接比較高度，避免 Math.round 造成的跨平台差異
    // 使用小於號，表示只有在「超過」最大高度時才切分
    // 加入 1px 的容錯範圍，避免因為渲染差異導致誤判
    if (testHeight > effectiveMaxHeight + 1 && currentPageHTML) {
      // 超過限制，切到新頁
      // if (isFirstPage) {
      //   console.log('--- 第一頁切分 ---');
      //   console.log('testHeight (測試高度像素)', testHeight);
      //   console.log('effectiveMaxHeight + 1 (限制高度像素)', effectiveMaxHeight + 1);
      //   console.log('判斷: testHeight > effectiveMaxHeight + 1 => ' + testHeight + ' > ' + (effectiveMaxHeight + 1) + ' => true');
      // }
      pages.push(currentPageHTML);
      currentPageHTML = nodeContent;
    } else {
      currentPageHTML += nodeContent;
    }
  });
  
  // 最後一頁
  if (currentPageHTML) {
    pages.push(currentPageHTML);
  }
  
  // console.log('=== splitNodesIntoPages: 分頁完成 ===');
  // console.log('總頁數', pages.length);
  
  return pages;
};

/**
 * 跨 .text div 進行分頁，允許後續 .text div 的內容填充到前一個 .text div 的不滿頁面中
 * @param {Array<Element>} textElements - 所有 .text 元素
 * @param {Function} countPrecedingLineElements - 計算前置元素數量的函數
 * @param {Function} collectTextNodes - 收集節點的函數
 * @param {number} pageSize - 每頁行數
 * @param {Element} measureContainer - 用於測量的容器
 * @param {number} lineHeight - 行高
 * @param {number} titleHeight - .title 元素的實際高度（像素）
 * @param {number} titleLines - .title 元素占用的行數
 * @returns {Array<string>} 分頁後的 HTML 內容陣列
 */
export const splitTextElementsAcrossPages = (
  textElements,
  countPrecedingLineElements,
  collectTextNodes,
  pageSize,
  measureContainer,
  lineHeight,
  titleHeight = 0,
  titleLines = 0
) => {
  const pages = [];
  let currentPageHTML = '';
  
  const maxHeight = pageSize * lineHeight;
  const firstPageMaxHeight = titleHeight > 0 ? maxHeight - titleHeight : maxHeight;
  
  // console.log('=== splitTextElementsAcrossPages: 跨 .text 分頁計算開始 ===');
  // console.log('pageSize (每頁行數)', pageSize);
  // console.log('lineHeight (行高像素)', lineHeight);
  // console.log('maxHeight (每頁最大高度像素)', maxHeight);
  // console.log('titleHeight (title高度像素)', titleHeight);
  // console.log('titleLines (title行數)', titleLines);
  // console.log('firstPageMaxHeight (第一頁最大高度像素)', firstPageMaxHeight);
  // console.log('計算公式: maxHeight - titleHeight = ' + maxHeight + ' - ' + titleHeight + ' = ' + firstPageMaxHeight);
  
  textElements.forEach((textElement, textIndex) => {
    // 計算當前 .text 元素的前置元素數量（只計算 IMG）
    const precedingImgCount = countPrecedingLineElements(textElement);
    
    // 收集當前 .text 元素內的所有節點
    const nodes = collectTextNodes(textElement);
    
    // console.log('--- 處理第 ' + (textIndex + 1) + ' 個 .text 元素 ---');
    // console.log('precedingImgCount (前置圖片數量)', precedingImgCount);
    
    // 逐個處理節點，嘗試放入當前頁面
    nodes.forEach((node, nodeIndex) => {
      const nodeContent = node.content;
      
      if (!nodeContent) return;
      
      // 計算當前頁面可用的行數和高度
      // 重要修正：判斷是否為第一頁應該基於 pages.length === 0，而不是 currentPageHTML === ''
      // 因為在處理第一個 .text 元素時，即使 currentPageHTML 已有內容，只要 pages.length === 0，就還是第一頁
      const isFirstPage = pages.length === 0;
      const isFirstTextElement = textIndex === 0;
      const isNewPage = currentPageHTML === ''; // 這個用於判斷是否是新開始的頁面（用於添加 plain_text class）
      
      let availableLines = pageSize;
      let currentPageMaxHeight = maxHeight;
      
      if (isFirstPage && isFirstTextElement) {
        // 第一頁需要扣除前置圖片和 .title
        availableLines = pageSize - precedingImgCount - titleLines;
        currentPageMaxHeight = firstPageMaxHeight;
        
        // if (nodeIndex === 0) {
        //   console.log('--- 第一頁第一個節點 ---');
        //   console.log('availableLines (可用行數)', availableLines);
        //   console.log('計算公式: pageSize - precedingImgCount - titleLines = ' + pageSize + ' - ' + precedingImgCount + ' - ' + titleLines + ' = ' + availableLines);
        //   console.log('currentPageMaxHeight (第一頁最大高度像素)', currentPageMaxHeight);
        // } else {
        //   // 第一頁的後續節點也要輸出日誌
        //   console.log('--- 第一頁第 ' + (nodeIndex + 1) + ' 個節點 ---');
        //   console.log('availableLines (可用行數)', availableLines);
        //   console.log('currentPageMaxHeight (第一頁最大高度像素)', currentPageMaxHeight);
        // }
      } else if (isNewPage) {
        // 非第一頁但新頁面，只扣除前置圖片
        availableLines = pageSize - precedingImgCount;

        // if (nodeIndex === 0) {
          // console.log('--- 新頁面（非第一頁）第一個節點 ---');
          // console.log('availableLines (可用行數)', availableLines);
          // console.log('計算公式: pageSize - precedingImgCount = ' + pageSize + ' - ' + precedingImgCount + ' = ' + availableLines);
        // }
      }
      
      // 使用較小的值作為限制（高度限制和行數限制取較嚴格者）
      const maxHeightForLines = availableLines * lineHeight;
      const effectiveMaxHeight = Math.min(currentPageMaxHeight, maxHeightForLines);
      
      // if (isFirstPage && isFirstTextElement && nodeIndex === 0) {
      //   console.log('maxHeightForLines (行數限制對應的高度像素)', maxHeightForLines);
      //   console.log('計算公式: availableLines * lineHeight = ' + availableLines + ' * ' + lineHeight + ' = ' + maxHeightForLines);
      //   console.log('effectiveMaxHeight (有效最大高度像素)', effectiveMaxHeight);
      //   console.log('計算公式: Math.min(currentPageMaxHeight, maxHeightForLines) = Math.min(' + currentPageMaxHeight + ', ' + maxHeightForLines + ') = ' + effectiveMaxHeight);
      // }
      
      // 測量加入當前節點後的總高度
      const testHTML = `<div class="text">${currentPageHTML + nodeContent}</div>`;
      
      // 確保測量容器有正確的樣式（如果是第一頁，可能需要 plain_text class）
      const originalClassName = measureContainer.className;
      if (isNewPage && isFirstTextElement && !originalClassName.includes('plain_text')) {
        // 暫時添加 plain_text class 以確保樣式正確
        measureContainer.className = originalClassName + ' plain_text';
      }
      
      // 使用 setHTMLAndApplyBrStyles 方法，自動處理 <br> 標籤的樣式
      if (measureContainer.setHTMLAndApplyBrStyles) {
        measureContainer.setHTMLAndApplyBrStyles(testHTML);
      } else {
        measureContainer.innerHTML = testHTML;
        // 手動應用 br 樣式
        const brElements = measureContainer.querySelectorAll('br');
        brElements.forEach(br => {
          br.style.display = 'none';
        });
      }
      
      // 重要：強制瀏覽器重排（reflow），確保元素正確渲染
      // 讀取 offsetHeight 會觸發瀏覽器重排
      void measureContainer.offsetWidth; // 觸發重排
      
      // 修改：使用 measureContainer.offsetHeight 作為主要測量方式
      // 這樣可以正確測量包含嵌套 <div> 的內容高度
      // 與 splitNodesIntoPages 保持一致
      const testHeight = measureContainer.offsetHeight;
      
      // 如果 offsetHeight 為 0，嘗試使用內部 .text 元素作為備用
      let finalTestHeight = testHeight;
      if (testHeight === 0) {
        const textElement = measureContainer.querySelector('.text');
        if (textElement) {
          void textElement.offsetWidth; // 觸發重排
          finalTestHeight = textElement.offsetHeight || textElement.scrollHeight;
          if (finalTestHeight === 0) {
            const rect = textElement.getBoundingClientRect();
            finalTestHeight = rect.height;
          }
        }
      }
      
      // 恢復原始 class
      if (isNewPage && isFirstTextElement) {
        measureContainer.className = originalClassName;
      }
      
      // 添加詳細的測量日誌（第一頁的所有節點都輸出）
      // if (isFirstPage && isFirstTextElement) {
      //   console.log('--- 第一頁第 ' + (nodeIndex + 1) + ' 個節點測量 ---');
      //   console.log('currentPageHTML 長度:', currentPageHTML.length, '字符');
      //   console.log('nodeContent 長度:', nodeContent.length, '字符');
      //   console.log('nodeContent 內容:', nodeContent.substring(0, 50));
      //   console.log('measureContainer.offsetHeight (主要測量):', testHeight);
      //   console.log('finalTestHeight (最終測量高度):', finalTestHeight);
      //   console.log('effectiveMaxHeight (有效最大高度像素)', effectiveMaxHeight);
      //   console.log('effectiveMaxHeight + 1 (限制高度像素)', effectiveMaxHeight + 1);
      //   console.log('判斷條件: finalTestHeight > effectiveMaxHeight + 1');
      //   console.log('判斷結果:', finalTestHeight > effectiveMaxHeight + 1);
      //   if (finalTestHeight > effectiveMaxHeight + 1) {
      //     console.warn('⚠️ 超過限制，將觸發切分！');
      //   }
      // }
      
      // 如果超過限制且當前頁面已有內容，則切到新頁
      // 修改：即使 currentPageHTML 為空，如果單個節點就超過限制，也要處理
      if (finalTestHeight > effectiveMaxHeight + 1) {
        if (currentPageHTML) {
          // 當前頁面已有內容，保存當前頁面並開始新頁面
          // if (isFirstPage && isFirstTextElement) {
          //   console.log('--- 第一頁切分觸發（累積內容超過限制）---');
          //   console.log('finalTestHeight (測試高度像素)', finalTestHeight);
          //   console.log('effectiveMaxHeight + 1 (限制高度像素)', effectiveMaxHeight + 1);
          //   console.log('超出高度:', finalTestHeight - (effectiveMaxHeight + 1), '像素');
          //   console.log('當前頁面內容長度:', currentPageHTML.length, '字符');
          //   console.log('判斷: finalTestHeight > effectiveMaxHeight + 1 => ' + finalTestHeight + ' > ' + (effectiveMaxHeight + 1) + ' => true');
          // }

          pages.push(currentPageHTML);
          currentPageHTML = nodeContent;
        } else {
          
          // 當前頁面為空，但單個節點就超過限制
          // 這種情況應該很少見，但還是要處理：強制放入當前頁面
          // 因為單個節點無法再切分
          // if (isFirstPage && isFirstTextElement) {
          //   console.warn('⚠️ 警告：第一頁第一個節點就超過限制！');
          //   console.warn('finalTestHeight (測試高度像素)', finalTestHeight);
          //   console.warn('effectiveMaxHeight + 1 (限制高度像素)', effectiveMaxHeight + 1);
          //   console.warn('超出高度:', finalTestHeight - (effectiveMaxHeight + 1), '像素');
          //   console.warn('節點內容:', nodeContent.substring(0, 100));
          // }
          
          currentPageHTML = nodeContent;
        }
      } else {
        // 可以放入當前頁面
        currentPageHTML += nodeContent;
        
        // 如果是最後一個節點且是第一頁，輸出最終測量結果
        // if (isFirstPage && isFirstTextElement && nodeIndex === nodes.length - 1) {
        //   console.log('--- 第一頁最終測量結果 ---');
        //   console.log('最終 finalTestHeight (像素)', finalTestHeight);
        //   console.log('effectiveMaxHeight (像素)', effectiveMaxHeight);
        //   console.log('可用行數限制:', availableLines, '行');
        //   console.log('可用高度限制:', effectiveMaxHeight, '像素');
        //   console.log('實際內容高度:', finalTestHeight, '像素');
        //   console.log('實際內容行數約:', Math.ceil(finalTestHeight / lineHeight), '行');
        //   console.log('是否超出:', finalTestHeight > effectiveMaxHeight + 1);
        //   if (finalTestHeight > effectiveMaxHeight + 1) {
        //     console.warn('⚠️ 警告：第一頁內容超出限制！');
        //     console.warn('超出高度:', finalTestHeight - (effectiveMaxHeight + 1), '像素');
        //     console.warn('超出行數約:', Math.ceil((finalTestHeight - (effectiveMaxHeight + 1)) / lineHeight), '行');
        //   } else {
        //     console.log('✅ 第一頁內容在限制範圍內');
        //     console.log('剩餘可用高度:', effectiveMaxHeight - finalTestHeight, '像素');
        //     console.log('剩餘可用行數約:', Math.floor((effectiveMaxHeight - finalTestHeight) / lineHeight), '行');
        //   }
        // }
      }
    });
  });
  
  // 保存最後一頁
  if (currentPageHTML) {
    pages.push(currentPageHTML);
  }
  
  // console.log('=== splitTextElementsAcrossPages: 分頁完成 ===');
  // console.log('總頁數', pages.length);
  
  return pages;
};

/**
 * 用分頁結果替換所有 .text 元素
 * @param {Array<Element>} textElements - 原始 .text 元素陣列
 * @param {Array<string>} pages - 分頁後的 HTML 內容陣列
 */
export const replaceAllTextElementsWithPages = (textElements, pages) => {
  if (textElements.length === 0 || pages.length === 0) return;
  
  const firstTextElement = textElements[0];
  const parent = firstTextElement.parentNode;
  
  // 在移除元素之前，找到第一個 .text 元素之後的第一個非 .text 兄弟節點
  // 這樣即使移除所有 .text 元素，這個參考仍然有效
  let insertBeforeTarget = null;
  let node = firstTextElement.nextSibling;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('text')) {
      insertBeforeTarget = node;
      break;
    }
    node = node.nextSibling;
  }
  
  // 創建所有新頁元素
  const newPages = pages.map(page => {
    const div = firstTextElement.ownerDocument.createElement('div');
    div.className = 'text';
    div.innerHTML = page;
    return div;
  });
  
  // 移除所有原始的 .text 元素
  textElements.forEach(textElement => {
    if (textElement.parentNode) {
      textElement.parentNode.removeChild(textElement);
    }
  });
  
  // 將新頁按順序插入
  if (insertBeforeTarget) {
    // 如果有找到插入位置，從前往後插入
    // 每次插入後，insertBeforeTarget 仍然有效（因為我們是在它之前插入）
    newPages.forEach(page => {
      parent.insertBefore(page, insertBeforeTarget);
    });
  } else {
    // 如果沒有找到插入位置，直接 append 到 parent
    newPages.forEach(page => {
      parent.appendChild(page);
    });
  }
};

/**
 * 用多個 .text 元素替換原 .text 元素（在 DOM 文檔中）
 */
export const replaceTextElementWithPages = (textElement, pages) => {
  const parent = textElement.parentNode;
  const nextSibling = textElement.nextSibling;
  
  // 創建新頁元素
  const newPages = pages.map(page => {
    const div = textElement.ownerDocument.createElement('div');
    div.className = 'text';
    div.innerHTML = page;
    return div;
  });
  
  // 移除原來的 .text 元素
  textElement.parentNode.removeChild(textElement);
  
  // 將新頁按順序插入
  let insertBeforeTarget = nextSibling;
  newPages.forEach(page => {
    if (insertBeforeTarget) {
      parent.insertBefore(page, insertBeforeTarget);
      insertBeforeTarget = page.nextSibling;
    } else {
      parent.appendChild(page);
      insertBeforeTarget = page.nextSibling;
    }
  });
};
